import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { ALLERGENS_FRAGMENT, WEIGHT_FRAGMENT, FOOD_ENERGY_FRAGMENT } from "../../fragments/menuItem";

import type {
  MenuItemNewResp,
  MenuItemNewVariables,
  MenuItemVariables,
  MenuItem
} from "../../types/menuItemTypes";

const CREATE_MENU_ITEM = gql`
  mutation CreateMenuItem($attributes: CreateMenuItemInput!) {
    createMenuItem(attributes: $attributes) {
      menuItem {
        menuItemLangs {
          name
          description
          language
        }
        menuSectionIds
        vegan
        chili
        hot
        price
        menuId
        updatedAt
        ...AllergensFragment
        ...WeightFragment
        ...FoodEnergyFragment
      }
      errors
    }
  }
  ${ALLERGENS_FRAGMENT}
  ${WEIGHT_FRAGMENT}
  ${FOOD_ENERGY_FRAGMENT}
` 

type UseCreateMenuItemOptions = {
  onCompleted?: (menuItem: MenuItem) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<MenuItemNewResp, MenuItemNewVariables>>[1];
};

const useCreateMenuItem = (options?: UseCreateMenuItemOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    MenuItemNewResp,
    MenuItemNewVariables
  >(CREATE_MENU_ITEM, options?.mutationOptions);

  const createMenuItem = useCallback(
    async (input: MenuItemVariables) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              sugarFree: input.sugarFree,
              glutenFree: input.glutenFree,
              cornFree: input.cornFree,
              dairyFree: input.dairyFree,
              eggsFree: input.eggsFree,
              fishFree: input.fishFree,
              legumesFree: input.legumesFree,
              peanutFree: input.peanutFree,
              seedsFree: input.seedsFree,
              shellfishFree: input.shellfishFree,
              treeNutsFree: input.treeNutsFree,
              vegan: input.vegan,
              chili: input.chili,
              hot: input.hot,
              amount: input.amount,
              mass: input.mass,
              unit: input.unit,
              amountInBase: input.amountInBase,
              price: input.price,
              image: input.image,
              menuId: input.menuId,
              menuItemLangs: input.menuItemLangs,
              menuSectionIds: input.menuSectionIds,
              proteins: input.proteins,
              fats: input.fats,
              carbohydrates: input.carbohydrates,
              calories: input.calories,
            },
          },
        });

        const createdMenuItem = result?.data?.createMenuItem;

        if (createdMenuItem?.errors?.length) {
          throw new Error(createdMenuItem.errors.join(", "));
        }

        if (options?.onCompleted && createdMenuItem?.menuItem) {
          options.onCompleted(createdMenuItem.menuItem);
        }

        return createdMenuItem?.menuItem;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const menuItem = data?.createMenuItem?.menuItem;

  return {
    createMenuItem,
    createdMenuItem: menuItem,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useCreateMenuItem
